import type { Board } from "../board";
import type { DamageType } from "../damageType";
import { DestroyableObstacle } from "../destroyableObstacle";
import { PRIORITY_LAST, type ParticleEmitter, type ProcessableThing } from "../interfaces";
import { ParticleType } from "../particleType";
import { deserializeXY } from "../serialization";
import type { Thing } from "../thing";
import type { XY } from "../xy";
import { isGasPassable } from "./baseGas";
import { BileGas } from "./bileGas";
import { GoldDrop } from "./goldDrop";

const EMISSION_RATE = 7;
const BURST_AMOUNT = 2;

export class ToxicSlugBrokenShell extends DestroyableObstacle implements ProcessableThing, ParticleEmitter {
	static key = "ToxicSlugBrokenShell";
	static readonly EMISSION_TURNS = 20;
	private emissionTurnsRemaining: number = ToxicSlugBrokenShell.EMISSION_TURNS;
	gold: number = 0;

	override isGasPermeable(): boolean {
		return true;
	}

	processTurn(board: Board): void {
		if (this.emissionTurnsRemaining <= 0) return;
		this.emissionTurnsRemaining--;
		BileGas.addOrPressurize(board, this.xy, EMISSION_RATE);
	}

	getProcessPriority(): number {
		return PRIORITY_LAST;
	}

	// Releases the leftover toxins and pays out the slug's bounty.
	protected override onZeroHp(source: Thing | null, type: DamageType | null): void {
		const board = this.board!;

		BileGas.addOrPressurize(board, this.xy, BURST_AMOUNT);
		for (const neighbor of this.xy.getNeighbors()) {
			if (isGasPassable(board, neighbor)) {
				BileGas.addOrPressurize(board, neighbor, BURST_AMOUNT);
			}
		}
		if (this.gold > 0) {
			GoldDrop.spawnOrMerge(board, this.xy, this.gold);
		}
		super.onZeroHp(source, type);
	}

	// ParticleEmitter
	getParticleType(): ParticleType | null {
		return this.emissionTurnsRemaining > 0 ? ParticleType.BILE_SMOKE : null;
	}

	serialize(): Record<string, unknown> {
		const data: Record<string, unknown> = {
			...super.serialize(),
			emissionTurnsRemaining: this.emissionTurnsRemaining,
		};
		if (this.gold > 0) {
			data.gold = this.gold;
		}
		return data;
	}

	static deserialize(data: any): ToxicSlugBrokenShell {
		const s = new ToxicSlugBrokenShell(deserializeXY(data.xy));
		s.restoreBaseFields(data);
		s.emissionTurnsRemaining = data.emissionTurnsRemaining;
		s.gold = data.gold ?? 0;
		return s;
	}
}
